import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  useTheme,
  Button,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { formatDate } from "../../../../../utils/fileUtils";
import FileStatus, {
  fileStatus,
} from "../../../DataExplorer/FileExplorer/FilesList/FileStatus";
import { getFileIconImage } from "../../../../../utils/fileUtils";
import ConfirmationDialog from "../../../../../components/ConfirmationDialog";
import EditFileDialog from "../../../DataExplorer/FileExplorer/EditFileDialog";
import {
  DeleteRounded,
  EditRounded,
  MoreVertRounded,
  AddCommentRounded,
  TagRounded,
  DownloadRounded,
} from "@mui/icons-material";
import EditCommentsDialog from "./EditCommentsDialog";
import EditTagsDialog from "./EditTagsDialog";
import { NavLink } from "react-router-dom";
import { VIEW_FILE_URL } from "../../../../../utils/routes";
import { usePostHog } from "posthog-js/react";
import {
  CLICK_ACTION_MENU_ON_DATA_MANAGEMENT,
  DOWNLOAD_FILE,
} from "../../../../../utils/posthogEvents";

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    width: "100%",
    borderRadius: 8,
    backgroundColor: theme.palette.background.dark,
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    cursor: "pointer",
    "&:last-child td": {
      borderBottom: "none",
    },
  },
  tableCell: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  fileIcon: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(1),
  },
}));

function FilesList({
  filesData,
  handleDeleteFile,
  handleUpdateFile,
  handleSelectFile,
  selectedStatus,
}) {
  const { files } = filesData;
  const { classes } = useStyles();
  const filesList = files || [];
  return (
    <Box>
      <Typography variant="h6">
        {fileStatus[selectedStatus].label} Files
      </Typography>
      <Box mb={2} />
      <TableContainer className={classes.tableContainer}>
        <Table sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell} sx={{ flex: 1, minWidth: 120 }}>Name</TableCell>
              <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
              <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>Date Modified</TableCell>
              <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>Category</TableCell>
              <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>Tags</TableCell>
              {/* <TableCell
                sx={{
                  minWidth: 80,
                  maxWidth: 220,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}>
                Comment
              </TableCell> */}
              <TableCell className={classes.tableCell} align="right" sx={{ whiteSpace: "nowrap" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filesList.map((file, index) => (
              <File
                key={`file-${index}`}
                file={file}
                handleDeleteFile={handleDeleteFile}
                handleUpdateFile={handleUpdateFile}
                handleSelectFile={handleSelectFile}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function File({ file, handleDeleteFile, handleUpdateFile, handleSelectFile }) {
  const { classes } = useStyles();
  const {
    name,
    created_at,
    file_type,
    status,
    tags,
    directory,
    comment,
    category,
  } = file;

  const tagsList = tags
    ? [tags.split(",")[0]].filter((tag) => tag && tag.trim() !== "")
    : [];

  const handleReviewClick = (e) => {
    // e.stopPropagation();
    const baseUrl = "https://mmd-annotator.vercel.app/annotate/";
    const fileId = file.id;
    const fileName = encodeURIComponent(file.name);
    const pdfUrl = encodeURIComponent(file.file_url);
    const fileType = encodeURIComponent(file.file_type);
    const mmdS3Url = file.mmd_lines_url
      ? encodeURIComponent(file.mmd_lines_url)
      : "none";
    const url = `${baseUrl}${fileId}?mmdFilename=${fileName}&pdfFilename=${fileName}&mmdS3Url=${mmdS3Url}&pdfS3Url=${pdfUrl}&page=1&fileId=${fileId}&file_type=${fileType}`;
    window.open(url, "_blank");
  };

  return (
    <TableRow
      component={NavLink}
      to={`${VIEW_FILE_URL}/${file.id}`}
      style={{ textDecoration: "none" }}
      className={classes.tableRow}>
      <TableCell
        className={classes.tableCell}
        sx={{ flex: 1, minWidth: 120, maxWidth: "none" }}>
        <Box display="flex" alignItems="center" gap={1}>
          <img
            src={getFileIconImage(file_type)}
            alt="file_icon"
            className={classes.fileIcon}
          />
          <Box minWidth={0} width="100%">
            <Tooltip title={name} arrow>
              <Typography
                variant="body2"
                noWrap
                textOverflow={"ellipsis"}
                sx={{ width: "100%", display: "block" }}>
                {name}
              </Typography>
            </Tooltip>
            <Tooltip title={directory} arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                textOverflow={"ellipsis"}
                sx={{ width: "100%", display: "block" }}>
                {directory}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>
        <FileStatus status={status} />
      </TableCell>
      <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>
        {formatDate(created_at)}
      </TableCell>
      <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>
        {category && (
          <Chip
            size="small"
            key={category}
            label={category}
            sx={{ borderRadius: 1, fontWeight: 500 }}
          />
        )}
      </TableCell>
      <TableCell className={classes.tableCell} sx={{ whiteSpace: "nowrap" }}>
        {tagsList.length > 0 && (
          <Box display={"flex"} gap={1} ml={1.5} flexShrink={0}>
            {tagsList.slice(0, 2).map((tag) => (
              <Chip
                size="small"
                key={tag}
                label={tag}
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
            ))}
          </Box>
        )}
      </TableCell>
      {/* <TableCell
        className={classes.tableCell}
        sx={{
          minWidth: 100,
          maxWidth: 220,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}>
        {comment}
      </TableCell> */}
      <TableCell
        className={classes.tableCell}
        align="right"
        sx={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="flex-end">
          {status === "reviewing" && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleReviewClick(e);
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "none",
              }}>
              Review
            </Button>
          )}
          <FileActionsMenu
            file={file}
            edge={null}
            handleDeleteFile={handleDeleteFile}
            handleUpdateFile={handleUpdateFile}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function FileActionsMenu({
  file,
  edge = "end",
  handleDeleteFile,
  handleUpdateFile,
}) {
  const { id } = file;
  const theme = useTheme();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditCommentsDialogOpen, setIsEditCommentsDialogOpen] =
    useState(false);
  const [isEditTagsDialogOpen, setIsEditTagsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const posthog = usePostHog();

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    posthog.capture(CLICK_ACTION_MENU_ON_DATA_MANAGEMENT);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleDownloadFile = async () => {
    try {
      const response = await fetch(file.file_url);
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      posthog.capture(DOWNLOAD_FILE);
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <IconButton
        component="div"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={handleClick}
        size="small"
        edge="end"
        sx={edge === "end" ? { marginRight: -1.5, marginTop: -0.5 } : {}}>
        <MoreVertRounded fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClose(e);
        }}
        autoFocus={false}
        disableAutoFocusItem
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
        <MenuItem
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditTagsDialogOpen(true);
          }}>
          <ListItemIcon>
            <TagRounded />
          </ListItemIcon>
          <Typography>Add Tag</Typography>
        </MenuItem>
        <MenuItem
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditCommentsDialogOpen(true);
          }}>
          <ListItemIcon>
            <AddCommentRounded />
          </ListItemIcon>
          <Typography>Add Comment</Typography>
        </MenuItem>
        <MenuItem
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadFile();
          }}>
          <ListItemIcon>
            <DownloadRounded />
          </ListItemIcon>
          <Typography>Download File</Typography>
        </MenuItem>
        <MenuItem
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditDialogOpen(true);
          }}>
          <ListItemIcon>
            <EditRounded />
          </ListItemIcon>
          <Typography>Rename File</Typography>
        </MenuItem>
        <MenuItem
          sx={{ color: theme.palette.error.main }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteDialogOpen(true);
          }}>
          <ListItemIcon>
            <DeleteRounded sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          <Typography>Delete File</Typography>
        </MenuItem>
      </Menu>
      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title={"Delete File"}
          content={`Do you really want to delete this file? This will permanently delete it from the account.`}
          noLabel={"cancel"}
          yesLabel={"yes, delete"}
          onNo={() => setIsDeleteDialogOpen(false)}
          onYes={() => handleDeleteFile(id)}
          isDelete={true}
        />
      )}
      {isEditDialogOpen && (
        <EditFileDialog
          file={file}
          handleUpdateFile={handleUpdateFile}
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
      {isEditCommentsDialogOpen && (
        <EditCommentsDialog
          file={file}
          handleUpdateFile={handleUpdateFile}
          onClose={() => setIsEditCommentsDialogOpen(false)}
        />
      )}
      {isEditTagsDialogOpen && (
        <EditTagsDialog
          file={file}
          handleUpdateFile={handleUpdateFile}
          onClose={() => setIsEditTagsDialogOpen(false)}
        />
      )}
    </>
  );
}

export default FilesList;
